import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Alert, Row, Col, Form, Button, Modal, Badge } from "react-bootstrap";
import { FiEye } from "react-icons/fi";
import { getNotificationLogs } from "../../services/adminApi";
import ReusableAdminTable from "./ReusableAdminTable";
import "./AdminNotificationLogs.css";

const AdminNotificationLogs = () => {
    // =========================================
    // STATES
    // =========================================
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [channelFilter, setChannelFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // =========================================
    // FETCH LOGS
    // =========================================
    const loadNotificationLogs = useCallback(async () => {
        setIsLoading(true);
        setError("");
        try {
            const res = await getNotificationLogs();
            const responseData = res?.data?.data || [];
            setLogs(responseData);
            setFilteredLogs(responseData);
        } catch (err) {
            console.error("Error loading notification logs:", err);
            setError("Failed to load notification logs.");
            setLogs([]);
            setFilteredLogs([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // =========================================
    // INITIAL LOAD
    // =========================================
    useEffect(() => {
        loadNotificationLogs();
    }, [loadNotificationLogs]);

    // =========================================
    // FILTER LOGIC
    // =========================================
    useEffect(() => {
        let filteredData = [...logs];

        // Search Filter
        if (search) {
            filteredData = filteredData.filter((log) =>
                (log.template_name || log.subject || log.recipient || "")
                    .toLowerCase()
                    .includes(search.toLowerCase())
            );
        }

        // Channel Filter
        if (channelFilter) {
            filteredData = filteredData.filter((log) => log.channel === channelFilter);
        }

        // Status Filter
        if (statusFilter) {
            filteredData = filteredData.filter((log) => log.status === statusFilter);
        }

        setFilteredLogs(filteredData);
    }, [search, channelFilter, statusFilter, logs]);

    // =========================================
    // VIEW PREVIEW
    // =========================================
    const handlePreview = (log) => {
        setSelectedLog(log);
        setShowModal(true);
    };

    // =========================================
    // TABLE COLUMNS
    // =========================================
    const columns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: "#",
                cell: (info) => info.row.index + 1,
            },
            {
                accessorKey: "user_id",
                header: "User ID",
            },
            {
                accessorKey: "template_name",
                header: "Template",
            },
            {
                accessorKey: "channel",
                header: "Channel",
                cell: (info) => <Badge bg="info">{info.getValue() || "N/A"}</Badge>,
            },
            {
                accessorKey: "recipient",
                header: "Recipient",
            },
            {
                accessorKey: "subject",
                header: "Subject",
            },
                        {
                id: "preview",
                header: () => <div style={{ textAlign: "center", width: "100%" }}>PREVIEW</div>,
                cell: (info) => (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: "0 auto" }}>
                        {/* ✅ PREVIEW BUTTON: ROYAL BLUE BACKGROUND */}
                        <button
                            type="button"
                            style={{
                                background: "#2563eb",
                                backgroundColor: "#2563eb",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "6px",
                                width: "32px",
                                height: "32px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                padding: 0,
                                flexShrink: 0
                            }}
                            onClick={() => handlePreview(info.row.original)}
                            title="Preview Notification"
                        >
                            <FiEye size={15} color="#ffffff" />
                        </button>
                    </div>
                ),
            },
            {
                accessorKey: "status",
                header: "Status",
                cell: (info) => {
                    const status = info.getValue();
                    return (
                        <Badge
                            bg={
                                status === "sent"
                                    ? "success"
                                    : status === "failed"
                                    ? "danger"
                                    : "warning"
                            }
                        >
                            {status || "Pending"}
                        </Badge>
                    );
                },
            },
            {
                accessorKey: "error_message",
                header: "Error Message",
                cell: (info) => <span className="text-danger">{info.getValue() || "-"}</span>,
            },
        ],
        []
    );

    // =========================================
    // RENDER
    // =========================================
    return (
        <div>
            {/* FILTERS */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Control
                        type="text"
                        placeholder="Search template, subject, recipient..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col md={3}>
                    <Form.Select
                        value={channelFilter}
                        onChange={(e) => setChannelFilter(e.target.value)}
                        className="dropdown-filter"
                    >
                        <option value="">All Channels</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="push">Push</option>
                    </Form.Select>
                </Col>
                <Col md={3}>
                    <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="dropdown-filter"
                    >
                        <option value="">All Status</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                        <option value="pending">Pending</option>
                    </Form.Select>
                </Col>
            </Row>

            {/* ERROR */}
            {error && <Alert variant="danger">{error}</Alert>}

            {/* TABLE */}
            <ReusableAdminTable
                data={filteredLogs}
                columns={columns}
                isLoading={isLoading}
                title="Notification Logs"
                searchPlaceholder="Search notification logs..."
                exportCsvFilename="notification-logs.csv"
            />

            {/* PREVIEW MODAL */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedLog?.subject || "Notification Preview"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div
                        className="notification-preview-container"
                        style={{
                            border: "1px solid #e5e5e5",
                            borderRadius: "10px",
                            padding: "15px",
                            background: "#ffffff",
                            color: "#222222",
                            maxHeight: "70vh",
                            overflowY: "auto",
                        }}
                        dangerouslySetInnerHTML={{
                            __html: selectedLog?.content || "",
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminNotificationLogs;